/** Prepare at most one queued passage while the previous passage is audible. */
export interface SpeechPreparation {
  text: string;
  voice: string;
}

export interface SpeechTicket {
  request: SpeechPreparation;
  eligible: () => boolean;
  pending?: Promise<string | undefined>;
  retired: boolean;
  allowLong?: boolean;
}

// A preparation is streamed: it is ready once its first chunk is, and the
// passage that takes it plays the rest as the worker writes it. One still
// without a first chunk when its turn comes is waited for: speaking the
// passage afresh would cancel that work and start it again from nothing.

export class SpeechLookahead {
  private tickets = new Set<SpeechTicket>();
  private preparing: SpeechTicket | undefined;
  private playing = false;

  constructor(
    private readonly transport: {
      prepare: (request: SpeechPreparation) => Promise<string>;
      discard: (id: string) => Promise<unknown>;
    },
  ) {}

  register(
    request: SpeechPreparation,
    eligible: () => boolean,
    allowLong = false,
  ): SpeechTicket {
    const ticket: SpeechTicket = {
      request,
      eligible,
      retired: false,
      allowLong,
    };
    this.tickets.add(ticket);
    this.pump();
    return ticket;
  }

  playback(active: boolean): void {
    this.playing = active;
    if (active) this.pump();
  }

  prune(): void {
    for (const ticket of this.tickets) {
      if (!ticket.eligible()) this.retire(ticket);
    }
  }

  reset(): void {
    this.playing = false;
    for (const ticket of this.tickets) this.retire(ticket);
  }

  async take(ticket: SpeechTicket): Promise<string | undefined> {
    // A passage no longer to be spoken does not wait for its preparation:
    // retiring discards that once it is ready.
    if (ticket.retired || !ticket.eligible()) {
      this.retire(ticket);
      return undefined;
    }
    this.tickets.delete(ticket);
    const id = await ticket.pending;
    if (this.preparing === ticket) this.preparing = undefined;
    if (ticket.retired || !ticket.eligible()) {
      if (id) await this.discard(id);
      return undefined;
    }
    ticket.retired = true;
    return id;
  }

  private discard(id: string): Promise<unknown> {
    return this.transport.discard(id).catch(() => undefined);
  }

  private retire(ticket: SpeechTicket): void {
    this.tickets.delete(ticket);
    if (ticket.retired) return;
    ticket.retired = true;
    void ticket.pending?.then(async (id) => {
      if (id) await this.discard(id);
      if (this.preparing === ticket) this.preparing = undefined;
      this.pump();
    });
  }

  private pump(): void {
    this.prune();
    if (!this.playing || this.preparing) return;
    const ticket = this.tickets.values().next().value as
      | SpeechTicket
      | undefined;
    // Full long replies keep native chunk streaming: the lookahead is for the
    // modest groups a streamed reply is spoken in.
    if (!ticket || (!ticket.allowLong && ticket.request.text.length > 360))
      return;
    this.preparing = ticket;
    ticket.pending = this.transport
      .prepare(ticket.request)
      .catch(() => undefined);
  }
}
